import { clientPosToWorldPos, getUniqueId } from '@/editor/utils';
import { EEntityType } from '@/editor/types';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { EEntityStatus } from '@/editor/types/entity';
import { getDefaultDoorWindowData } from './utils';
import { EDoorWindowType, IWindowData } from '@/editor/data/model/model';
import { CommonController2d } from '../CommonController';
import WindowModel from '@/editor/entities/2d/windowModel';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateWindowDoorRulerInfos } from '@/editor/stores/utils/ruler/windowDoor';
import { WindowAbsorbCalculator } from '@/editor/manager/absorb/window/windowAbsorbCalculator';
import { cloneDeep } from 'lodash-es';
import { getWindowAbsorbWallInfos } from '@/editor/manager/absorb/window/utils';

export class DrawWindow extends CommonController2d {
  private _originalDoorWindowData:
    | (IWindowData & { selected: boolean; status: EEntityStatus; isEntrance: boolean })
    | null = null;

  private _movedDoorWindowData:
    | (IWindowData & { selected: boolean; status: EEntityStatus; isEntrance: boolean })
    | null = null;

  private _virtualWindowDoorModel: WindowModel;
  private _rulerModel: RulerModel;

  private _adsorbCalculator: WindowAbsorbCalculator;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this.initData();

    this._virtualWindowDoorModel = new WindowModel({
      id: '',
      type: this._originalDoorWindowData?.type || EDoorWindowType.window
    });
    this._rulerModel = new RulerModel();

    this._adsorbCalculator = new WindowAbsorbCalculator({ getWallInfos: getWindowAbsorbWallInfos });
    this._adsorbCalculator.updateWallData();
  }

  private initData() {
    let dwData;
    if (this._gs.editorStateStore.windowDoorVrtualData) {
      dwData = this._gs.editorStateStore.windowDoorVrtualData;
      this._gs.editorStateStore.windowDoorVrtualData = undefined;
    } else {
      dwData = getDefaultDoorWindowData();
    }

    this._originalDoorWindowData = dwData
      ? {
          ...dwData,
          selected: false,
          status: EEntityStatus.unnormal,
          isEntrance: false
        }
      : null;
    this._movedDoorWindowData = cloneDeep(this._originalDoorWindowData);
  }

  override mouseMove(e: MouseEvent) {
    if (!this._movedDoorWindowData || !this._originalDoorWindowData) return;

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPositionV2 = clientPosToWorldPos(e, camera, renderer);

    const absorbInfo = this._adsorbCalculator.calculate({
      rawPoint: worldPositionV2,
      margin: this._movedDoorWindowData.width / 2,
      side: this._movedDoorWindowData.side
    });

    if (!absorbInfo) {
      // 没有吸附到墙
      this._movedDoorWindowData.relativeWallInfo = { relativeWallId: '' };
      this._movedDoorWindowData.center = worldPositionV2;
      this._movedDoorWindowData.rotation = this._originalDoorWindowData.rotation;
      this.updateModel();
      return;
    }

    let uniformWallData: { id: string; thickness: number } | undefined;
    if (absorbInfo.wallType === EEntityType.Wall) {
      const _wallData = globalStores.wallStore.getWallData(absorbInfo.wallId);
      if (!_wallData) return;
      uniformWallData = { id: _wallData.id, thickness: _wallData.baseData.thickness };
    } else {
      const _wallData = globalStores.separateWallStore.getSepData(absorbInfo.wallId, false);
      if (!_wallData) return;
      uniformWallData = { id: _wallData.id, thickness: _wallData.thickness };
    }

    this._movedDoorWindowData = {
      ...this._movedDoorWindowData,
      relativeWallInfo: { relativeWallId: uniformWallData.id, wallType: absorbInfo.wallType },
      thickness: uniformWallData.thickness,
      rotation: absorbInfo.rotation,
      center: absorbInfo.absorbPoint,
      distance: absorbInfo.distance
    };

    this.updateModel();
  }

  override mouseUp(e: MouseEvent, isDragged: boolean) {
    if (isDragged) return;

    if (e.button === 2) {
      // 退出编辑模式
      this.dispose();
      this.exit();
      return;
    }

    if (e.button !== 0 || !this._movedDoorWindowData) return;

    this._gs.windowDoorOperation.addWindowDoor({ ...this._movedDoorWindowData, id: getUniqueId() });
  }

  private updateModel() {
    if (!this._movedDoorWindowData) {
      this._virtualWindowDoorModel.clear();
      this._rulerModel.clear();
      return;
    }

    this._movedDoorWindowData.status = this._movedDoorWindowData.relativeWallInfo.relativeWallId
      ? EEntityStatus.normal
      : EEntityStatus.unnormal;
    this._virtualWindowDoorModel.update(this._movedDoorWindowData);

    this._rulerModel.updateModel(calculateWindowDoorRulerInfos(this._movedDoorWindowData));
  }

  clear() {
    this._originalDoorWindowData = null;
    this._movedDoorWindowData = null;

    this._virtualWindowDoorModel.clear();
    this._rulerModel.clear();
  }

  override dispose() {
    this.clear();

    this._virtualWindowDoorModel.dispose();
    this._rulerModel.dispose();

    super.dispose();
  }
}
